import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    X,
    Droplets,
    Wind,
    Zap,
    Clock,
    AlertTriangle,
    Navigation,
    Phone,
    FileText,
    Camera,
    ClipboardList
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { productionTrendService, ProductionTrend, TankLevel } from '../../utils/services/productionTrendService';
import { format } from 'date-fns';

interface SmartWellCardProps {
    well: any;
    onClose: () => void;
    onNavigate?: () => void;
    onCall?: () => void;
    onAddEntry?: () => void;
    onCreateTicket?: () => void;
    onCreateTask?: () => void;
    onOpenTanks?: () => void;
    onOpenCams?: () => void;
}

export default function SmartWellCard({
    well,
    onClose,
    onNavigate,
    onCall,
    onAddEntry,
    onCreateTicket,
    onCreateTask,
    onOpenTanks,
    onOpenCams
}: SmartWellCardProps) {
    const [trend, setTrend] = useState<ProductionTrend | null>(null);
    const [tanks, setTanks] = useState<TankLevel[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'tanks' | 'actions'>('overview');

    // Trigger tanks tab if onOpenTanks is called purely as a prop (optional, but good for linking)
    useEffect(() => {
        if (onOpenTanks) {
            // If parent wants to force open tanks, we could listen here. 
            // But for now, we leave local state control.
        }
    }, [onOpenTanks]);

    useEffect(() => {
        let mounted = true;
        const loadData = async () => {
            if (!well?.id || !well?.companyId) return;
            try {
                const [trendData, tankData] = await Promise.all([
                    productionTrendService.getWellTrend(well.id, well.companyId),
                    productionTrendService.getTankLevels(well.id, well.companyId)
                ]);
                if (mounted) {
                    setTrend(trendData);
                    setTanks(tankData);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to load well intelligence', error);
                if (mounted) setLoading(false);
            }
        };
        loadData();
        return () => { mounted = false; };
    }, [well]);

    const isCritical = well.status === 'critical' || well.hasAlert;
    const isActive = ['active', 'producing', 'flowing'].includes(well.status?.toLowerCase());

    // Mock data if real data is missing (for demo)
    const currentOil = trend?.avgOil || Math.floor(well.oilProduction || 0);
    const currentGas = trend?.avgGas || Math.floor(well.gasProduction || 0);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 z-50 pointer-events-auto"
        >
            <Card className="w-[380px] bg-slate-950/95 backdrop-blur-xl border border-blue-500/30 text-white shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden rounded-2xl ring-1 ring-white/10">

                {/* Animated Gradient Border */}
                <div className={`h-1 w-full bg-gradient-to-r ${isCritical ? 'from-red-500 via-orange-500 to-red-500' :
                    isActive ? 'from-emerald-500 via-teal-500 to-emerald-500' :
                        'from-blue-500 via-cyan-500 to-blue-500'
                    } animate-gradient-x`} />

                {/* Header */}
                <div className="p-4 relative">
                    <div className="absolute top-0 right-0 p-4">
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white rounded-full hover:bg-white/10" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <div className={`h-12 w-12 rounded-xl flex items-center justify-center border shadow-lg ${isCritical ? 'bg-red-500/20 border-red-500/50 text-red-400' :
                                isActive ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' :
                                    'bg-slate-800 border-slate-700 text-slate-400'
                                }`}>
                                {isCritical ? <AlertTriangle className="h-6 w-6" /> : <Droplets className="h-6 w-6" />}
                            </div>
                            {isActive && (
                                <span className="absolute -bottom-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                </span>
                            )}
                        </div>

                        <div className="flex-1 min-w-0 pr-6">
                            <h3 className="font-bold text-lg text-white truncate leading-tight">{well.name}</h3>
                            <p className="text-xs text-blue-300 font-mono mt-0.5 truncate">{well.apiNumber || 'API N/A'}</p>

                            <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className={`text-[10px] h-5 px-1.5 border ${isCritical ? 'border-red-500/50 text-red-400 bg-red-950/30' :
                                    'border-blue-500/30 text-blue-300 bg-blue-950/30'
                                    }`}>
                                    {well.status?.toUpperCase() || 'UNKNOWN'}
                                </Badge>
                                {trend?.lastUpdated && (
                                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                        <Clock className="h-3 w-3" />
                                        <span>{format(trend.lastUpdated, 'MMM d, HH:mm')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-white/10 px-4 gap-4">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`text-xs font-medium py-2 border-b-2 transition-colors ${activeTab === 'overview' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                    >
                        OVERVIEW
                    </button>
                    <button
                        onClick={() => setActiveTab('tanks')}
                        className={`text-xs font-medium py-2 border-b-2 transition-colors ${activeTab === 'tanks' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                    >
                        TANKS ({tanks.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('actions')}
                        className={`text-xs font-medium py-2 border-b-2 transition-colors ${activeTab === 'actions' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                    >
                        ACTIONS
                    </button>
                </div>

                <div className="p-4 bg-gradient-to-b from-slate-900/50 to-slate-950/80 min-h-[160px]">
                    <AnimatePresence mode="wait">

                        {/* === OVERVIEW TAB === */}
                        {activeTab === 'overview' && (
                            <motion.div
                                key="overview"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                className="space-y-4"
                            >
                                {/* Metrics Grid */}
                                <div className="grid grid-cols-3 gap-2">
                                    <MetricBox label="OIL" value={currentOil} unit="BBL/D" icon={Droplets} color="blue" />
                                    <MetricBox label="GAS" value={currentGas} unit="MCF/D" icon={Wind} color="purple" />
                                    <MetricBox label="SPM" value={well.spm || (Math.random() * 5 + 4).toFixed(1)} unit="AVG" icon={Zap} color="amber" />
                                </div>

                                {/* Sparkline */}
                                {trend && trend.dataPoints.length > 1 && (
                                    <div className="h-16 w-full -mx-1">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={trend.dataPoints}>
                                                <defs>
                                                    <linearGradient id="colorOil" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <Area type="monotone" dataKey="oil" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorOil)" isAnimationActive={true} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* === TANKS TAB === */}
                        {activeTab === 'tanks' && (
                            <motion.div
                                key="tanks"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className="space-y-3"
                            >
                                {tanks.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500 text-xs">No tank telemetry available.</div>
                                ) : (
                                    tanks.map((tank, idx) => (
                                        <div key={tank.tankId || idx} className="bg-slate-900/50 rounded-lg p-2 border border-white/5">
                                            <div className="flex justify-between items-end mb-1">
                                                <span className="text-xs font-medium text-slate-300">{tank.tankName}</span>
                                                <span className="text-lg font-bold text-white">{tank.currentLevel}' <span className="text-xs text-slate-500">/ {tank.capacity}'</span></span>
                                            </div>
                                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full ${tank.percentage > 85 ? 'bg-red-500' : 'bg-blue-500'}`}
                                                    style={{ width: `${tank.percentage}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between mt-1 text-[10px] text-slate-500">
                                                <span>{tank.percentage}% Full</span>
                                                <span>Fill in ~{tank.daysUntilFull} days</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </motion.div>
                        )}

                        {/* === ACTIONS TAB === */}
                        {activeTab === 'actions' && (
                            <motion.div
                                key="actions"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className="grid grid-cols-2 gap-2"
                            >
                                <ActionButton onClick={onNavigate} icon={Navigation} label="Navigate" color="blue" />
                                <ActionButton onClick={onCall} icon={Phone} label="Call Pumper" color="green" />
                                <ActionButton onClick={onAddEntry} icon={Droplets} label="Add Entry" color="amber" />
                                <ActionButton onClick={onCreateTicket} icon={FileText} label="Create Ticket" color="purple" />
                                <ActionButton onClick={onCreateTask} icon={ClipboardList} label="Assign Task" color="pink" />
                                <ActionButton onClick={onOpenCams} icon={Camera} label="Live Cams" color="slate" />
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>

                {/* Footer Actions (Quick Access) */}
                {activeTab !== 'actions' && (
                    <div className="p-3 bg-slate-950 border-t border-white/10 flex gap-2">
                        <Button className="flex-1 bg-blue-600 hover:bg-blue-500 text-xs h-8" onClick={onNavigate}>NAV</Button>
                        <Button className="flex-1 bg-slate-800 hover:bg-slate-700 text-xs h-8" onClick={onAddEntry}>ENTRY</Button>
                        <Button className="flex-1 bg-amber-900/50 hover:bg-amber-800/50 text-amber-500 border border-amber-500/30 text-xs h-8" onClick={onOpenTanks}>TANKS</Button>
                        <Button className="flex-1 bg-slate-800 hover:bg-slate-700 text-xs h-8" onClick={() => setActiveTab('actions')}>MORE</Button>
                    </div>
                )}
            </Card>
        </motion.div>
    );
}

// Subcomponents
function MetricBox({ label, value, unit, icon: Icon, color }: any) {
    const colors = {
        blue: 'text-blue-400 bg-blue-500/5 group-hover:bg-blue-500/10',
        purple: 'text-purple-400 bg-purple-500/5 group-hover:bg-purple-500/10',
        amber: 'text-amber-400 bg-amber-500/5 group-hover:bg-amber-500/10'
    };

    return (
        <div className="bg-slate-900/50 rounded-lg p-2 border border-white/5 relative group overflow-hidden">
            <div className={`absolute inset-0 transition-colors ${colors[color as keyof typeof colors].split(' ')[1]}`} />
            <div className="relative z-10">
                <div className={`flex items-center gap-1.5 mb-0.5 ${colors[color as keyof typeof colors].split(' ')[0]}`}>
                    <Icon className="h-3 w-3" />
                    <span className="text-[10px] font-bold tracking-wider">{label}</span>
                </div>
                <div className="text-xl font-bold text-white tracking-tight">{value}</div>
                <div className="text-[9px] text-slate-500">{unit}</div>
            </div>
        </div>
    );
}

function ActionButton({ onClick, icon: Icon, label, color }: any) {
    const colorClass = {
        blue: 'hover:bg-blue-500/20 text-blue-400',
        green: 'hover:bg-emerald-500/20 text-emerald-400',
        amber: 'hover:bg-amber-500/20 text-amber-400',
        purple: 'hover:bg-purple-500/20 text-purple-400',
        pink: 'hover:bg-pink-500/20 text-pink-400',
        slate: 'hover:bg-slate-700/50 text-slate-300'
    };

    return (
        <button
            onClick={onClick}
            className={`flex flex-col items-center justify-center p-3 rounded-lg bg-slate-900/50 border border-white/5 transition-all ${colorClass[color as keyof typeof colorClass]}`}
        >
            <Icon className="h-5 w-5 mb-1" />
            <span className="text-xs font-semibold">{label}</span>
        </button>
    );
}
